import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CreditCard, RefreshCw } from "lucide-react";

import { apiPostAuthed } from "../lib/api";
import { Card, LinkButton, StatusPill } from "./ui";

type BillingResultPageProps = {
  result: "success" | "cancelled";
};

type WebSession = {
  accessToken: string;
};

export function BillingResultPage({ result }: BillingResultPageProps) {
  const isSuccess = result === "success";
  const sessionId = useMemo(() => new URLSearchParams(window.location.search).get("session_id") ?? "", []);
  const [syncState, setSyncState] = useState<"idle" | "syncing" | "synced" | "error">("idle");
  const [syncMessage, setSyncMessage] = useState("");

  useEffect(() => {
    if (!isSuccess || !sessionId) return;

    let active = true;
    const rawSession = window.localStorage.getItem("ibox-web-session");
    let session: Partial<WebSession> | null = null;
    try {
      session = rawSession ? (JSON.parse(rawSession) as Partial<WebSession>) : null;
    } catch {
      session = null;
    }

    if (!session?.accessToken) {
      setSyncMessage("Sign in to your iBox account to refresh subscription status.");
      return;
    }

    setSyncState("syncing");
    apiPostAuthed<{ synced: true }>("/billing/sync-checkout", { sessionId }, session.accessToken)
      .then(() => {
        if (!active) return;
        setSyncState("synced");
        setSyncMessage("Your iBox Pro subscription is active.");
      })
      .catch((error) => {
        if (!active) return;
        setSyncState("error");
        setSyncMessage(error instanceof Error ? error.message : "iBox could not refresh subscription status yet.");
      });

    return () => {
      active = false;
    };
  }, [isSuccess, sessionId]);

  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-45" />
        <div className="absolute inset-0 bg-bg/34" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-4xl place-items-center px-6 py-24">
        <Card interactive={false} className="w-full p-8">
          <div className="flex items-start justify-between gap-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-bg text-primary">
              <CreditCard size={26} strokeWidth={1.6} />
            </div>
            <StatusPill status={isSuccess ? "success" : "warning"} label={isSuccess ? "CHECKOUT COMPLETE" : "CHECKOUT CANCELLED"} />
          </div>

          <h1 className="mt-10 font-display text-5xl font-bold uppercase leading-none text-text">
            {isSuccess ? "Return to iBox" : "No Changes Made"}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted">
            {isSuccess
              ? "Stripe accepted the checkout. iBox is refreshing your account subscription now."
              : "Checkout was cancelled before a subscription was created. You can return to iBox and start again anytime."}
          </p>

          {isSuccess && syncMessage ? (
            <div
              className={`mt-6 flex items-center gap-3 rounded-lg border border-border bg-surface-2 px-4 py-3 text-sm font-semibold ${
                syncState === "error" ? "text-warning" : "text-success"
              }`}
            >
              {syncState === "syncing" ? <RefreshCw className="animate-spin" size={14} /> : null}
              {syncMessage}
            </div>
          ) : null}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <LinkButton href="/download">
              Open iBox <ArrowRight size={14} />
            </LinkButton>
            <LinkButton href="/account" variant="ghost">
              Account
            </LinkButton>
          </div>
        </Card>
      </section>
    </main>
  );
}
